import React, { useCallback, useMemo, useState } from 'react';
import { CategoryDto } from 'types';
import { DraggableStageItem } from './DraggableStageItem';
import { DraggableStageItemOverlay } from './DraggableStageItemOverlay';
import { makeCatalogChangeCategoriesOrderAction, makeCatalogFetchCategoriesSuccessAction } from 'state/catalog/actions';
import { SortableContext } from '@dnd-kit/sortable';
import { Stage } from '../Stage/Stage';
import { Text, TextTypography } from '../Text/Text';
import { useAction } from 'core/store/store';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    SensorDescriptor,
    SensorOptions,
    useDroppable,
} from '@dnd-kit/core';
import i18n from '../../../i18n';

export interface DraggableProductListHeaderProps {
    activeTab: number;
    setActiveTab: (value: number) => void;
    categories: CategoryDto[];
    onTabClick: (categoryId: string) => void;
    sensors: SensorDescriptor<SensorOptions>[];
}

const productListHeaderStyle = { paddingLeft: 0 };
const productListDividerCustomStyles = { margin: 0 };

export const DraggableProductListHeader = (props: DraggableProductListHeaderProps) => {
    const { activeTab, setActiveTab, categories, onTabClick, sensors } = props;

    const [dragableItem, setDraggableItem] = useState<CategoryDto>(null);
    const { t } = i18n.useTranslation();

    const action = useAction();

    const { setNodeRef: categoryDropZoneRef } = useDroppable({
        id: 'droppableCategory',
    });

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            setDraggableItem(categories.find(category => category.categoryId === event.active.id));
        },
        [categories, setDraggableItem],
    );
    const handleDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            if (!over || active.id === over.id) {
                return;
            }

            action(makeCatalogChangeCategoriesOrderAction, active.id, over.id);

            let currentDragableItemIndex: number;
            let directionItemIndex: number;

            categories.some((category, index) => {
                if (currentDragableItemIndex != null && directionItemIndex != null) {
                    return true;
                }
                if (category.categoryId === active.id) {
                    currentDragableItemIndex = index;
                }
                if (category.categoryId === over.id) {
                    directionItemIndex = index;
                }
            });

            const movedItemEntity = categories.splice(currentDragableItemIndex, 1);
            categories.splice(directionItemIndex, 0, movedItemEntity[0]);
            action(makeCatalogFetchCategoriesSuccessAction, categories);
            setDraggableItem(null);
        },
        [categories, setDraggableItem],
    );

    const categoryIds = useMemo(() => categories.map(category => category.categoryId), categories);

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext items={categoryIds}>
                <Stage
                    value={activeTab}
                    showScrollButtons={true}
                    onChange={setActiveTab}
                    styles={productListHeaderStyle}
                    customDividerStyles={productListDividerCustomStyles}
                    innerRef={categoryDropZoneRef}
                >
                    {categories.map(category => {
                        const { categoryId, name } = category;
                        return (
                            <DraggableStageItem
                                key={categoryId}
                                entityKey={categoryId}
                                to=""
                                label={
                                    <Text shouldCollapseText typography={TextTypography.BODY_HEADING}>
                                        {t(name)}
                                    </Text>
                                }
                                onClick={() => onTabClick(categoryId)}
                            />
                        );
                    })}
                </Stage>
            </SortableContext>
            <DragOverlay>
                {dragableItem ? (
                    <DraggableStageItemOverlay
                        label={
                            <Text shouldCollapseText typography={TextTypography.BODY_HEADING}>
                                {dragableItem.name}
                            </Text>
                        }
                    />
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};
